import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getBlogPostById,
  updateBlogPost,
} from "@/modules/blog/blogService";
import { db } from "../../firebase/firebase";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import "../styles/BlogEditor.css";

// Define the expected shape of a blog post
type BlogPost = {
  title: string;
  slug: string;
  description: string;
  content: string;
  author: string;
  keywords?: string[];
  state?: string;
  category?: string;
};

const states = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado",
  "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho",
  "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana", "Maine",
  "Maryland", "Massachusetts", "Michigan", "Minnesota", "Mississippi",
  "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey",
  "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio",
  "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina",
  "South Dakota", "Tennessee", "Texas", "Utah", "Vermont", "Virginia",
  "Washington", "West Virginia", "Wisconsin", "Wyoming",
];

const categories = [
  "Installation",
  "Technology",
  "Economics",
  "Maintenance"
];

export default function BlogEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [form, setForm] = useState({
    title: "",
    slug: "",
    description: "",
    content: "",
    author: "",
    keywords: "",
    state: "",
    category: "",
  });

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isEditMode) return;

    const fetchData = async () => {
      try {
        const raw = await getBlogPostById(id!);
        const data = raw as BlogPost;

        if (!data) {
          setError("Blog post not found.");
          return;
        }

        setForm({
          title: data.title,
          slug: data.slug,
          description: data.description,
          content: data.content,
          author: data.author,
          keywords: data.keywords?.join(", ") || "",
          state: data.state ?? "",
          category: data.category ?? "",
        });
      } catch (err) {
        setError("Error loading blog post. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, isEditMode]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    try {
      const commonPayload = {
        title: form.title,
        slug: form.slug,
        description: form.description,
        content: form.content,
        author: form.author,
        keywords: form.keywords.split(",").map((k) => k.trim()),
        state: form.state,
        category: form.category,
      };

      if (isEditMode) {
        await updateBlogPost(id!, {
          ...commonPayload,
          datePublished: new Date().toISOString(),
        });
      } else {
        await addDoc(collection(db, "blogPosts"), {
          ...commonPayload,
          datePublished: serverTimestamp(),
        });
      }

      navigate("/admin/blog");
    } catch (error) {
      setError("Error saving blog post. Please try again.");
      console.error("Error saving blog post:", error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading">Loading post...</div>;

  return (
    <div className="admin-container">
      <h1>{isEditMode ? "Edit Blog Post" : "New Blog Post"}</h1>

      {error && <div className="error-message">{error}</div>}

      <form className="admin-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="title">Title</label>
          <input
            type="text"
            id="title"
            name="title"
            placeholder="Enter blog post title"
            value={form.title}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="slug">Slug</label>
          <input
            type="text"
            id="slug"
            name="slug"
            placeholder="e.g. solar-in-utah"
            value={form.slug}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="author">Author</label>
          <input
            type="text"
            id="author"
            name="author"
            placeholder="Author name"
            value={form.author}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label htmlFor="keywords">Keywords</label>
          <input
            type="text"
            id="keywords"
            name="keywords"
            placeholder="Comma-separated keywords"
            value={form.keywords}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label htmlFor="state">State</label>
          <select
            id="state"
            name="state"
            value={form.state}
            onChange={handleChange}
            required
          >
            <option value="">Select a State</option>
            {states.map((state) => (
              <option key={state} value={state}>
                {state}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="category">Category</label>
          <select
            id="category"
            name="category"
            value={form.category}
            onChange={handleChange}
            required
          >
            <option value="">Select a Category</option>
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group full-width">
          <label htmlFor="description">Meta Description</label>
          <textarea
            id="description"
            name="description"
            placeholder="Enter meta description for SEO"
            value={form.description}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group full-width">
          <label htmlFor="content">Content</label>
          <textarea
            id="content"
            name="content"
            placeholder="Enter blog post content"
            value={form.content}
            onChange={handleChange}
            required
          />
        </div>

        <button type="submit" disabled={saving}>
          {saving
            ? isEditMode
              ? "Saving Changes..."
              : "Creating Post..."
            : isEditMode
            ? "Save Changes"
            : "Create Post"}
        </button>
      </form>
    </div>
  );
}
